import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getCurrentUser, signInWithRedirect, signOut, fetchUserAttributes, fetchAuthSession, updateUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import './Header.css';
import { jwtDecode } from 'jwt-decode';
import { sendLogToN8n } from '../index';
import { fetchCognitoUserInfo, pickNameFromUserInfo } from '../utils/cognitoUserInfo';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const getUserAndAttributes = async () => {
      try {
        const currentUser = await getCurrentUser();
        let attributes = {};
        let fallbackName = undefined;
        let claimNameCandidate = undefined;
        try {
          attributes = await fetchUserAttributes();
        } catch (e) {
          // attribute fetch 실패해도 무시
        }
        // attributes가 비어 있으면 idToken에서 직접 name 참조
        if (!attributes || Object.keys(attributes).length === 0) {
          try {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            if (idToken) {
              const claims = jwtDecode(idToken);
              if (claims) {
                claimNameCandidate =
                  claims.name || claims.nickname || claims.profile_nickname || claims.preferred_username || undefined;
                fallbackName = claimNameCandidate || fallbackName;
              }
              // attributes에도 넣어서 코드의 통일성 보장
              attributes = { ...(attributes || {}), ...(claims || {}) };
            }
          } catch (err) {
            // ignore
          }
        }

        // 카카오(OIDC) 등에서 name 속성이 비어있으면, 토큰 claim의 닉네임을 Cognito user attribute(name)에 1회 저장
        try {
          let candidate =
            attributes?.name ||
            claimNameCandidate ||
            attributes?.nickname ||
            attributes?.profile_nickname ||
            attributes?.preferred_username ||
            undefined;

          // claim에 닉네임이 없으면 Cognito userInfo로 한번 더 시도 (카카오에서 여기로 오는 경우가 있음)
          if (!candidate) {
            try {
              const session = await fetchAuthSession();
              const accessToken = session.tokens?.accessToken?.toString();
              const userInfo = await fetchCognitoUserInfo(accessToken);
              candidate = pickNameFromUserInfo(userInfo) || candidate;
              if (candidate) attributes = { ...(attributes || {}), nickname: String(candidate) };
            } catch (e) {
              // ignore
            }
          }

          // 이미 name이 있거나, 저장할 후보가 없으면 skip
          if (!attributes?.name && candidate) {
            const key = `cognito_name_sync_done:${currentUser?.userId || currentUser?.username || 'user'}`;
            const done = sessionStorage.getItem(key);
            if (!done) {
              await updateUserAttributes({ userAttributes: { name: String(candidate) } });
              sessionStorage.setItem(key, '1');
              // 로컬 상태도 즉시 반영 (UI 깜빡임 방지)
              attributes = { ...(attributes || {}), name: String(candidate) };
            }
          }
        } catch (e) {
          // name이 write 불가이거나 사용자 풀에 없는 경우 등은 조용히 무시
        }

        setUser({ ...currentUser, attributes, fallbackName });
      } catch (error) {
        setUser(null);
      }
    };
    getUserAndAttributes();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          window.location.reload();
          break;
        case 'signedOut':
          setUser(null);
          break;
        default:
          break;
      }
    });

    return unsubscribe;
  }, []);

  // user 로그인(생성) 후 프로필 API 호출용 useEffect
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        return;
      }
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (idToken) {
          // NOTE: 프로필 API는 서버측에서 플랜/상태 동기화 목적(부수효과)로 호출하며,
          // 프론트에서는 응답값을 사용하지 않는다.
          await fetch('https://1cw148rxb8.execute-api.ap-northeast-2.amazonaws.com/api/get-profile', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${idToken}`,
            },
          });
        } else {
          // ignore
        }
      } catch (err) {
        // ignore
      }
    };
    fetchProfile();
  }, [user]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSignUp = () => {
    sendLogToN8n({ type: 'click', log: { button: '회원가입', location: window.location.pathname } });
    // NOTE: These values are from the Amplify configuration in src/index.js
    const domain = 'ap-northeast-2mq1rnuvky.auth.ap-northeast-2.amazoncognito.com';
    const clientId = '5i61j6hmvsubje5en96ull5mr6';
    const redirectUri = window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://flowto-ai.com';
    const responseType = 'code';
    const signUpUrl = `https://${domain}/signup?response_type=${responseType}&client_id=${clientId}&redirect_uri=${redirectUri}`;
    window.location.assign(signUpUrl);
  };

  const handleSignOut = async () => {
    setIsMenuOpen(false);
    try {
      await signOut();
    } catch (error) {
      // ignore
    }
    try {
      const domain = 'ap-northeast-2mq1rnuvky.auth.ap-northeast-2.amazoncognito.com';
      const clientId = '5i61j6hmvsubje5en96ull5mr6';
      const logoutUri = window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://flowto-ai.com';
      const responseType = 'code';
      const logoutUrl = `https://${domain}/logout?response_type=${responseType}&client_id=${clientId}&logout_uri=${logoutUri}`;
      window.location.assign(logoutUrl);
    } catch (error) {
      // ignore
    }
  };

  const handleHomeClick = () => {
    // 모바일 메뉴 닫기
    setIsMenuOpen(false);
    sendLogToN8n({ type: 'click', log: { button: '홈', location: window.location.pathname } });

    // 홈으로 이동 시 상단으로 스크롤
    if (location.pathname === '/') {
      window.history.pushState(null, '', '/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLogin = () => {
    sendLogToN8n({ type: 'click', log: { button: '로그인', location: window.location.pathname } });
    // 로그인 시작 위치를 저장 (로그인 후 동일 페이지로 복귀)
    try {
      localStorage.setItem('login_redirect', window.location.pathname || '/');
    } catch (e) {
      // ignore
    }
    // Cognito 기본 로그인 화면을 건너뛰고 바로 Google 계정 선택으로 이동
    signInWithRedirect({ provider: 'Google' });
  };

  const handleNavLinkClick = (menuName) => {
    // 모바일 메뉴 닫기
    setIsMenuOpen(false);
    sendLogToN8n({ type: 'click', log: { button: menuName, location: window.location.pathname } });
  };

  // 현재 페이지 위치 확인 (메뉴 active 상태 표시용)
  const path = location.pathname;
  const isHome = path === '/';
  const isProducts = path === '/products' || path.startsWith('/products/');
  const isAbout = path === '/about';
  const isBlog = path === '/blog' || path === '/blog-service';
  const isContact = path === '/contact';

  return (
    <header className={`header${(scrolled || !isHome) ? ' header--scrolled' : ''}`}>
      <div className="header-container">
        <Link to="/" className="logo" onClick={handleHomeClick}>
          {/* CI 정본: DESIGN.md — 로고 그래픽 + Flowto.AI 워드마크 lockup (PNG) */}
          <img src="/flowto-logo-header.png" alt="Flowto.AI" className="logo-lockup" />
        </Link>

        <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
          <ul className="nav-list">
            <li className="nav-item">
              <Link
                to="/products"
                className={`nav-link ${isProducts ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('Products')}
              >
                Products
              </Link>
            </li>
            <li className="nav-item">
              <Link
                to="/about"
                className={`nav-link ${isAbout ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('About')}
              >
                About
              </Link>
            </li>
            <li className="nav-item">
              <Link
                to="/blog"
                className={`nav-link ${isBlog ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('Blog')}
              >
                Blog
              </Link>
            </li>
            <li className="nav-item">
              <Link
                to="/contact"
                className={`nav-link ${isContact ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('Contact')}
              >
                Contact
              </Link>
            </li>
            {/* 모바일 전용 사용자 정보 및 로그아웃 */}
            {user && (
              <>
                <li className="nav-item mobile-user-info">
                  <span className="mobile-user-greeting">{user?.attributes?.name || user?.fallbackName || user.username}님</span>
                </li>
                <li className="nav-item mobile-logout">
                  <button
                    className="nav-link nav-button mobile-logout-btn"
                    onClick={handleSignOut}
                  >
                    로그아웃
                  </button>
                </li>
              </>
            )}
          </ul>
        </nav>

        <div className="header-actions">
          {user ? (
            <>
              <span className="user-greeting" style={{ marginRight: '15px' }}>{user?.attributes?.name || user?.fallbackName || user.username}님</span>
              <button onClick={handleSignOut} className="btn-signup">로그아웃</button>
            </>
          ) : (
            <>
              <button onClick={handleLogin} className="btn-login">로그인</button>
              <button onClick={handleSignUp} className="btn-signup">회원가입</button>
            </>
          )}
        </div>

        <button className={`menu-toggle ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu} aria-label="메뉴 열기">
          <span className="hamburger"></span>
          <span className="hamburger"></span>
          <span className="hamburger"></span>
        </button>
      </div>
    </header>
  );
};

export default Header;
